'use client';

import { useEffect, useRef } from 'react';
import { useScheduler } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import { useRequiredInfoValidation } from '../utils/useRequiredInfoValidation';

/**
 * Hook that automatically navigates to the appropriate step based on context state.
 * This ensures that when resuming a draft, we land on the correct step based on what data exists.
 * Navigates when draft loading completes (isLoadingDraft transitions from true to false).
 *
 * CRITICAL: Only navigates once per draft load. Subsequent data updates (like Zillow loading)
 * will NOT trigger navigation to prevent interrupting user flow.
 *
 * Resuming used to jump straight to the Slots step whenever services,
 * contact and address existed, which walked the customer past the More
 * Information step they had abandoned. Unanswered required questions now hold the
 * resume point on the Property step.
 */
export function useAutoNavigateToStep() {
  const {
    selectedPrimaryService,
    contact,
    address,
    selectedDateTime,
    isLoadingDraft,
    shouldResumeDraft,
  } = useScheduler();
  const requiredInfo = useRequiredInfoValidation();
  const { setActiveStep, activeStep } = useStepNavigation();
  const wasLoadingRef = useRef(false);
  const hasNavigatedRef = useRef(false);
  const lastDraftDataRef = useRef<string>(''); // Track data hash to detect changes
  const shouldResumeDraftRef = useRef(false); // Track when shouldResumeDraft changes

  // Track when loading starts and reset navigation flag
  useEffect(() => {
    if (isLoadingDraft) {
      wasLoadingRef.current = true;
      hasNavigatedRef.current = false; // Reset navigation flag when loading starts
    }
  }, [isLoadingDraft]);

  // Track when shouldResumeDraft changes to true (explicit resume action)
  useEffect(() => {
    if (shouldResumeDraft) {
      // Always reset navigation state when shouldResumeDraft is true
      // This ensures we can navigate even if resuming multiple drafts in sequence
      // console.log('[useAutoNavigateToStep] Resume requested, resetting navigation state');
      hasNavigatedRef.current = false;
      lastDraftDataRef.current = ''; // Reset data hash to force navigation
      shouldResumeDraftRef.current = true;
    } else {
      shouldResumeDraftRef.current = false;
    }
  }, [shouldResumeDraft]);

  useEffect(() => {
    // Don't navigate while draft is loading
    if (isLoadingDraft) {
      return;
    }

    // CRITICAL: If we've already navigated AND this is not an explicit resume action, don't navigate again
    // This prevents navigation when Zillow updates address fields or other data changes
    // BUT: If shouldResumeDraft is true, we should navigate even if we've navigated before
    if (hasNavigatedRef.current && !shouldResumeDraft) {
      return;
    }

    // Create a hash of current draft data to detect when it changes
    const currentDataHash = JSON.stringify({
      selectedPrimaryService,
      contactId: contact?._id,
      addressStreet: address?.street,
      selectedDateTime,
    });

    // Navigate ONLY when loading completes (was loading, now not loading)
    // OR when shouldResumeDraft is true (explicit resume action)
    // This ensures we only navigate on the first data load after draft loading completes
    const justFinishedLoading = wasLoadingRef.current && !isLoadingDraft;
    const hasDraftData =
      selectedPrimaryService || contact || address || selectedDateTime;
    const dataChanged = currentDataHash !== lastDraftDataRef.current;
    const isExplicitResume = shouldResumeDraft && !hasNavigatedRef.current;

    // Only navigate if:
    // 1. We just finished loading a draft (CRITICAL: this is the only time we navigate)
    // 2. We have draft data
    // 3. Data has changed (new draft loaded) OR this is an explicit resume action
    // 4. We haven't navigated yet (already checked above, but keeping for clarity)
    const shouldNavigate =
      justFinishedLoading &&
      hasDraftData &&
      (dataChanged || isExplicitResume) &&
      !hasNavigatedRef.current;

    if (!shouldNavigate) {
      // console.log('[useAutoNavigateToStep] Not navigating:', {
      //   justFinishedLoading,
      //   hasDraftData,
      //   dataChanged,
      //   isExplicitResume,
      //   hasNavigatedRef: hasNavigatedRef.current,
      // });
      return;
    }

    // console.log('[useAutoNavigateToStep] ✅ Should navigate - conditions met');

    // Determine the appropriate step based on data completeness
    let targetStep = 1; // Default to Services step

    // Check completion in order
    const hasServices = !!selectedPrimaryService;
    const hasContact = !!contact;
    const hasProperty = !!address && !!address.street;
    // Note: hasDateTime is checked but datetime is NOT saved for drafts (to avoid holding slots)
    // So for draft resume, we typically go to Slots step if property exists
    const hasDateTime = !!selectedDateTime;

    // console.log('[useAutoNavigateToStep] Calculating target step:', {
    //   hasServices,
    //   hasContact,
    //   hasProperty,
    //   hasDateTime,
    //   currentStep: activeStep,
    // });

    // Navigate to the first incomplete step
    // Edge case: If draft has no data at all, stay on current step (don't navigate)
    if (!hasServices && !hasContact && !hasProperty && !hasDateTime) {
      // console.log('[useAutoNavigateToStep] No draft data, staying on current step');
      hasNavigatedRef.current = true; // Mark as handled to prevent re-evaluation
      lastDraftDataRef.current = currentDataHash; // Update data hash
      wasLoadingRef.current = false; // Reset loading flag
      return;
    }

    // Navigation logic: prioritize based on what's complete
    // Step mapping: 1=Services, 2=Property, 3=Schedule/Slots, 4=AdditionalContacts, 5=Summary
    // If services are missing, go to Services step
    if (!hasServices) {
      targetStep = 1; // Go to Services
    }
    // If services exist but contact is missing, go to Contact step (within Services step)
    else if (!hasContact) {
      targetStep = 1; // Go to Services/Contact step (services selected, need contact)
    }
    // If services and contact exist but property is missing, go to Property step
    else if (!hasProperty) {
      targetStep = 2; // Go to Property
    }
    // Property exists but the mandatory questions for the selected services are
    // still unanswered - hold on Property so the customer completes them
    else if (!requiredInfo.isValid) {
      targetStep = 2; // Go to Property (More Information)
    }
    // If services, contact, and property exist - go to Slots step (step 3)
    // Note: datetime is NOT saved for drafts, so we always go to slots if property exists
    else {
      targetStep = 3; // Go to Slots (Calendar) to select appointment time
    }

    console.log('[useAutoNavigateToStep] Target step calculated:', targetStep);

    // Only navigate if we're not already on the correct step
    if (targetStep !== activeStep) {
      console.log(
        '[useAutoNavigateToStep] ✅ Navigating from step',
        activeStep,
        'to step',
        targetStep
      );
      setActiveStep(targetStep);
    } else {
      console.log(
        '[useAutoNavigateToStep] Already on correct step:',
        targetStep
      );
    }

    // Mark as navigated and update tracking refs
    // This prevents any subsequent data changes from triggering navigation
    hasNavigatedRef.current = true;
    lastDraftDataRef.current = currentDataHash; // Update data hash
    wasLoadingRef.current = false; // Reset loading flag after navigation decision

    // Clear shouldResumeDraft flag after navigation (DraftLoader will handle this, but ensure it's cleared)
    if (shouldResumeDraft) {
      // Note: We don't clear it here - DraftLoader manages this flag
      // But we've already used it to force navigation, so we're done
    }
  }, [
    selectedPrimaryService,
    contact,
    address,
    selectedDateTime,
    isLoadingDraft,
    shouldResumeDraft, // Keep in deps but don't use in condition
    requiredInfo.isValid,
    activeStep,
    setActiveStep,
  ]);
}
